// AddItemSheet — top-level orchestrator for the AddItem rebuild.
//
// Sheet is HARD-LOCKED to a fixed height (vh + visualViewport-aware
// so it tracks the iOS keyboard) so pipeline transitions don't make
// the surface jump. ModalSheet's new `height` prop owns this.
//
// Flow:
//   1. Open → EntryGate tiles (Search / Scan / Photo / Browse).
//   2. User picks one → that pipeline mounts.
//   3. Inside the pipeline, a back affordance returns to EntryGate.
//
// `seed.mode` skips EntryGate when the launcher already knows what
// pipeline we want (e.g. App.jsx passes `{ mode: "scan", barcodeUpc }`
// from the floating barcode entry).

import { useCallback, useRef, useState } from "react";
import ModalSheet from "../../../components/ModalSheet";
import { createDraftStore } from "./store";
import EntryGate from "./EntryGate";
import WizardPipeline from "./WizardPipeline";
import PhotoPipeline from "./PhotoPipeline";
import ScanPipeline from "./ScanPipeline";
import { rememberCorrection } from "../../../lib/draftMemory";
import {
  findIngredient as findIngredientLite,
  getCanonicalHome,
  getCanonicalTile,
} from "../../../data/ingredients";
import { buildDisplayName } from "../../../lib/pantryHelpers";
import { font, color } from "../../../lib/mcmTokens";

export default function AddItemSheet({
  seed = { mode: "blank" },
  userId,
  isAdmin = false,
  onClose,
  onSubmit,
  onAddToShoppingList,
  title,
}) {
  const storeRef = useRef(null);
  // editingId — when seed.existingRow is supplied, we're in edit
  // mode. Mode locks to "manual" (skip EntryGate; the user already
  // has an item, they're tweaking it), the store hydrates from the
  // row, and on commit we hand a patch back to the caller instead
  // of an insert payload. Held on a ref so callers don't have to
  // re-pass on every render.
  const editingIdRef = useRef(null);
  if (!storeRef.current) {
    storeRef.current = createDraftStore();
    storeRef.current.setMeta({ pipeline: seed?.mode || "blank" });
    if (seed?.barcodeUpc) {
      storeRef.current.setMeta({ barcodeUpc: seed.barcodeUpc });
    }
    if (seed?.existingRow) {
      storeRef.current.seedFromRow(seed.existingRow);
      editingIdRef.current = seed.existingRow.id || null;
    }
    // AIRecipe "+ IN PANTRY" seed — passes a known canonical id +
    // display name so we land on the manual-edit surface with the
    // identity prefilled. The user just confirms quantity / brand
    // / location instead of re-typing what the recipe already knew.
    if (seed?.canonicalId && typeof storeRef.current.setCanonical === "function") {
      storeRef.current.setCanonical(
        seed.canonicalId,
        seed.name || null,
        null,            // emoji — registry will fill on resolve
        "ai-recipe-add", // source tag for telemetry
      );
      // Pre-seed location + tile from the canonical's natural home
      // (DB → bundled → hub-chain). Without this, a wizard launched
      // from "Tap to place" on arborio_rice would force the user to
      // navigate to pantry → pasta_grains again even though the
      // canonical already declares its home in data. Caller-supplied
      // seed.location / seed.tileId override the derivation — that's
      // how ResolveCard hands its already-resolved override down.
      const home = getCanonicalHome(seed.canonicalId, null);
      const seededLoc = seed.location || home?.location || null;
      if (seededLoc) {
        storeRef.current.setAxis("location", seededLoc, "autofill");
      }
      const seededTile =
        seed.tileId ||
        (seededLoc ? getCanonicalTile(seed.canonicalId, seededLoc, null) : null);
      if (seededTile) {
        storeRef.current.setAxis("tileId", seededTile, "autofill");
      }
    }
  }
  const store = storeRef.current;

  const [mode, setMode] = useState(() => {
    // Edit mode forces "manual" so the user lands directly on the
    // axis-edit surface instead of the EntryGate picker.
    // Manual pipeline removed 2026-05-04 — wizard is the universal
    // "non-scan / non-photo" path now (it covers search + browse).
    // Edit mode and any caller passing seed.mode="manual" route to
    // wizard instead.
    if (seed?.existingRow) return "wizard";
    if (!seed || !seed.mode || seed.mode === "blank") return "entry";
    if (seed.mode === "manual") return "wizard";
    if (["photo", "wizard", "scan"].includes(seed.mode)) return seed.mode;
    return "entry";
  });
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState(null);

  // Pipelines that have multi-step internal navigation (Wizard,
  // Scan, Photo) register a back-handler here. The header's back
  // button calls it first; if it returns truthy, the pipeline
  // handled the back internally (e.g. canonical → group → tile →
  // location). If it returns falsy/undefined, we fall through to
  // setMode("entry") so the user lands back at the EntryGate
  // instead of being kicked all the way out of the sheet.
  const backHandlerRef = useRef(null);
  const registerBack = useCallback((handler) => {
    backHandlerRef.current = handler;
  }, []);
  const handleBack = useCallback(() => {
    const handler = backHandlerRef.current;
    if (typeof handler === "function" && handler()) return;
    setMode("entry");
    backHandlerRef.current = null;
  }, []);

  store._onPlace = async () => {
    if (committing) return;
    setCommitting(true);
    setCommitError(null);
    try {
      const pantryRow = store.buildPantryRow();
      const draft = store.getState();
      // Edit mode: hand back { id, ...patch } so the caller can run
      // an update-by-id instead of an append. The id field is the
      // signal — App.jsx checks for it before deciding insert vs
      // update.
      const payload = editingIdRef.current
        ? { id: editingIdRef.current, ...pantryRow }
        : pantryRow;
      const r = onSubmit ? await onSubmit(payload) : null;

      if (userId && draft.canonicalId) {
        const axes = {
          canonicalId: draft.canonicalId || null,
          typeId:      draft.typeId || null,
          tileId:      draft.tileId || null,
          location:    draft.location || null,
          state:       draft.state || null,
          cut:         draft.cut || null,
          brand:       draft.brand?.trim() || null,
          name:        draft.name?.trim() || null,
          packageSizeAmount: Number.isFinite(Number(draft.packageAmount))
            ? Number(draft.packageAmount)
            : null,
          packageSizeUnit:   draft.packageUnit?.trim() || null,
          claims:            draft.claims || null,
          productMetadata:   draft.barcodeMetadata || null,
        };
        // Dual-write closes the brand-learning loop in both directions:
        //
        //   • UPC tier (barcode_identity_corrections) — "scan this UPC
        //     again next time → these axes" (existing behavior)
        //   • Brand tier (manual_corrections) — "this brand is associated
        //     with this canonical → surface it in the brand picker"
        //
        // When BOTH a UPC and a brand are bound, the row teaches BOTH
        // tiers. Without this, scanning a Barilla cavatappi UPC would
        // only update the UPC index — the next user wizard-adding
        // cavatappi would see no Barilla pill until manual brand picks
        // accumulated. With it, every scan-confirmed (canonical, brand)
        // pair flows into the picker the moment it commits.
        const writes = [];
        if (draft.barcodeUpc) {
          writes.push({ key: { upc: draft.barcodeUpc }, axes });
          if (draft.brand) {
            writes.push({
              key: { canonicalAndBrand: { canonical: draft.canonicalId, brand: draft.brand } },
              axes,
            });
          }
        } else if (draft.brand) {
          writes.push({
            key: { canonicalAndBrand: { canonical: draft.canonicalId, brand: draft.brand } },
            axes,
          });
        } else {
          writes.push({ key: { canonicalOnly: draft.canonicalId }, axes });
        }
        for (const w of writes) {
          rememberCorrection({ userId, isAdmin, ...w })
            .catch((e) => console.warn("[remember-correction]", e?.message || e));
        }
      }

      onClose?.(r);
    } catch (e) {
      setCommitError(e?.message || String(e));
      setCommitting(false);
    }
  };

  // Shopping-list path — same wizard, different destination. Builds
  // a shopping_list_items-shaped row from the draft and hands it to
  // the caller (App.jsx wires this to setShoppingList). Skips
  // rememberCorrection writes since no UPC scan / brand commit
  // happened — this path is just "I'm adding this to the list."
  store._onAddToList = async () => {
    if (committing) return;
    setCommitting(true);
    setCommitError(null);
    try {
      const draft = store.getState();
      const ing = draft.canonicalId
        ? findIngredientLite(draft.canonicalId)
        : null;
      const newId = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      // shopping_list_items has no brand/cut columns, so compose the
      // full identity into `name` via buildDisplayName — same composer
      // the kitchen card uses, so the row reads as
      // "Tyson Ground Chicken Breast (1 lb)" instead of bare "Chicken".
      const amount = Number.isFinite(Number(draft.packageAmount))
        ? Number(draft.packageAmount)
        : 1;
      const unit = draft.packageUnit?.trim() || "";
      const composed = buildDisplayName({
        canonicalId: draft.canonicalId || null,
        brand: draft.brand || null,
        state: draft.state || null,
        cut: draft.cut || null,
        amount,
        unit,
        name: draft.name?.trim() || null,
      });
      const listRow = {
        id: newId,
        ingredientId: draft.canonicalId || null,
        name: composed,
        // "" / "pantry" fallbacks, never null: shopping_list_items
        // keeps NOT NULL on emoji + category, and most canonicals
        // carry neither field (emoji-free rule; category retired for
        // hub chains). Explicit null defeats the column default and
        // the insert banners with a not-null violation.
        emoji: ing?.emoji || "",
        amount,
        unit,
        category: ing?.category || "pantry",
        source: "manual",
        priceCents: null,
      };
      const r = onAddToShoppingList ? await onAddToShoppingList(listRow) : null;
      onClose?.(r);
    } catch (e) {
      setCommitError(e?.message || String(e));
      setCommitting(false);
    }
  };

  return (
    <ModalSheet
      onClose={onClose}
      height="86lvh"
      maxHeight="86lvh"
      showHandle={false}
      showClose={false}
      swipeable={false}
    >
      <div style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        gap: 0,
        margin: "-4px -4px -22px",
      }}>
        {mode !== "entry" && (
          <div style={{
            flexShrink: 0,
            padding: "4px 14px 8px",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}>
            <button
              onClick={(e) => { e.preventDefault(); handleBack(); }}
              aria-label="Back"
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: "transparent",
                border: `1px solid ${color.hairline}`,
                color: color.inkMuted,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 2 4 7 9 12" />
              </svg>
            </button>
            {title && (
              <div
                style={{
                  flex: 1,
                  fontFamily: font.detail,
                  fontStyle: "italic",
                  fontSize: 15,
                  fontWeight: 500,
                  color: color.ink,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </div>
            )}
            {!title && <div style={{ flex: 1 }} />}
            <button
              onClick={(e) => { e.preventDefault(); onClose?.(); }}
              aria-label="Close"
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: "transparent",
                border: `1px solid ${color.hairline}`,
                color: color.inkMuted,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="3" x2="11" y2="11" />
                <line x1="11" y1="3" x2="3" y2="11" />
              </svg>
            </button>
          </div>
        )}

        {commitError && (
          <div style={{
            margin: "0 14px 8px",
            padding: "10px 12px",
            background: "rgba(216, 92, 78, 0.12)",
            border: "1px solid rgba(216, 92, 78, 0.4)",
            borderRadius: 10,
            fontFamily: font.detail,
            fontStyle: "italic",
            fontSize: 13,
            color: color.ink,
            flexShrink: 0,
          }}>
            Couldn't save: {commitError}
          </div>
        )}

        <div style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          padding: "0 14px 24px",
          display: "flex",
          flexDirection: "column",
        }}>
          {mode === "entry" && (
            <EntryGate
              onPick={(pick) => {
                store.setMeta({ pipeline: pick });
                setMode(pick);
              }}
              onClose={onClose}
            />
          )}
          {mode === "wizard" && (
            <WizardPipeline store={store} registerBack={registerBack} />
          )}
          {mode === "photo"  && (
            <PhotoPipeline store={store} onSwitchPipeline={setMode} registerBack={registerBack} />
          )}
          {mode === "scan"   && (
            <ScanPipeline store={store} onSwitchPipeline={setMode} registerBack={registerBack} isAdmin={isAdmin} />
          )}
        </div>
      </div>
    </ModalSheet>
  );
}

export { AddItemSheet as MCMAddDraftSheet };
